import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import TwitterImporter from './TwitterImporter';
import { processTweetData } from '../utils/tweetProcessor';
import SubscriptionService from '../services/SubscriptionService';
import './TweetGenerator.css';

// Helper function to extract username from Twitter URL
const extractUsernameFromUrl = (url) => {
  if (!url) return null;
  
  try {
    // Handle different Twitter URL formats
    const patterns = [
      /(?:twitter\.com|x\.com)\/([A-Za-z0-9_]+)\/status\/\d+/,
      /(?:twitter\.com|x\.com)\/([A-Za-z0-9_]+)(?:\/|\?|$)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1].toLowerCase();
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting username from URL:', error);
    return null;
  }
};

// Helper to decode HTML entities
function decodeHTMLEntities(text) {
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
}

const TweetGenerator = ({ onVideoGenerate, user, onUpgradeRequest }) => {
  const [tweetData, setTweetData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showAccountRestrictionModal, setShowAccountRestrictionModal] = useState(false);
  const [accountRestrictionData, setAccountRestrictionData] = useState(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaModalData, setQuotaModalData] = useState(null);
  const tweetRef = useRef(null);

  // Load user profile on component mount
  useEffect(() => {
    if (user?.uid) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      const subscription = await SubscriptionService.checkSubscriptionStatus(user.uid);
      setUserProfile(subscription);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleTweetImported = async (importedData) => {
    // Check if this is bulk generation
    if (importedData.type === 'bulk') {
      await handleBulkGeneration(importedData);
      return;
    }

    // Single tweet processing (existing logic)
    const mediaUrls = importedData.mediaUrls || [];
    
    // Map profilePicture to profileFile for the render
    const dataWithProfileFile = {
      ...importedData,
      profileFile: importedData.profilePicture,
      mediaUrls: mediaUrls, // Use array of media URLs
    };
    
    setTweetData(dataWithProfileFile);
    // Automatically generate video after tweet is imported
    await generateTweetImageAndVideo(dataWithProfileFile);
  };

  const handleBulkGeneration = async (bulkData) => {
    setIsGenerating(true);
    
    try {
      // STEP 1: Check quota limits FIRST before processing any tweets
      const videosToGenerate = bulkData.urls.length;
      const bulkValidation = await SubscriptionService.canGenerateBulkVideos(user.uid, videosToGenerate);
      
      if (!bulkValidation.canGenerate) {
        // Show custom quota modal instead of processing
        setQuotaModalData({
          videosRequested: videosToGenerate,
          videosRemaining: bulkValidation.videosRemaining || 0,
          excessCount: bulkValidation.excessCount || 0,
          reason: bulkValidation.reason,
          isUnlimited: bulkValidation.subscription?.isUnlimited || false
        });
        setShowQuotaModal(true);
        setIsGenerating(false);
        return;
      }

      // STEP 2: Validate account access for all URLs before processing
      const invalidUrls = [];
      const validUrls = [];
      
      for (const url of bulkData.urls) {
        const username = extractUsernameFromUrl(url);
        if (!username) {
          console.warn(`Could not extract username from URL: ${url}`);
          invalidUrls.push({ url, reason: 'Invalid URL format' });
          continue;
        }
        
        // Check if user can use this username (skip for unlimited users)
        if (userProfile && userProfile.twitterUsernames?.length > 0 && !userProfile.isUnlimited && !bulkValidation.subscription?.isUnlimited) {
          const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === username.toLowerCase());
          if (!hasAccess) {
            invalidUrls.push({ 
              url, 
              username, 
              reason: `No access to @${username}. You can only generate videos from: @${userProfile.twitterUsernames.join(', @')}`
            });
            continue;
          }
        }
        
        validUrls.push(url);
      }
      
      // If there are invalid URLs, show error and stop
      if (invalidUrls.length > 0) {
        // Set modal data and show custom modal instead of alert
        setAccountRestrictionData({
          invalidCount: invalidUrls.length,
          invalidUrls: invalidUrls,
          allowedAccounts: userProfile?.twitterUsernames || []
        });
        setShowAccountRestrictionModal(true);
        setIsGenerating(false);
        return;
      }
      
      // STEP 3: Now process tweet URLs (only after all validations pass)
      const tweetDataArray = [];
      
      for (const url of validUrls) {
        try {
          console.log(`Processing tweet URL: ${url}`);
          
          // Use the EXACT same processing logic as single tweets
          const processedTweetData = await processTweetData(url, bulkData.audioSelection);
          
          // Add profileFile for compatibility
          processedTweetData.profileFile = processedTweetData.profilePicture;
          
          // Add circle overlays for bulk processing
          processedTweetData.circleOverlays = bulkData.circleOverlays || [];
          
          tweetDataArray.push(processedTweetData);
          console.log(`Successfully processed tweet from @${processedTweetData.username}`);
          
        } catch (error) {
          console.error('Error processing tweet URL:', url, error);
          // Continue with other URLs even if one fails
        }
      }

      if (tweetDataArray.length === 0) {
        throw new Error('No valid tweets found to process');
      }

      console.log(`Successfully processed ${tweetDataArray.length} tweets. Starting video generation...`);

      // STEP 4: Generate videos for all tweets
      await generateBulkVideos(tweetDataArray);
      
    } catch (error) {
      console.error('Error in bulk generation:', error);
      setIsGenerating(false);
    }
  };



  const generateBulkVideos = async (tweetDataArray) => {
    console.log(`Starting bulk generation for ${tweetDataArray.length} tweets`);
    
    // Generate screenshots sequentially to avoid DOM conflicts
    const videoJobs = [];
    
    for (let i = 0; i < tweetDataArray.length; i++) {
      const tweetData = tweetDataArray[i];
      
      try {
        console.log(`Generating screenshot for tweet ${i + 1}/${tweetDataArray.length}: @${tweetData.username}`);
        
        // Set the current tweet for screenshot
        setTweetData(tweetData);
        
        // Wait longer for DOM to fully update and verify the tweet has changed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Double-check that the DOM has updated by checking the username
        let attempts = 0;
        const maxAttempts = 10;
        
        while (attempts < maxAttempts) {
          if (tweetRef.current) {
            const usernameElement = tweetRef.current.querySelector('.tweet_name');
            if (usernameElement && usernameElement.textContent === tweetData.name) {
              // DOM has updated correctly
              break;
            }
          }
          
          // Wait a bit more and try again
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (attempts >= maxAttempts) {
          console.warn(`DOM update verification failed for tweet ${i}, proceeding anyway`);
        }
        
        if (tweetRef.current) {
          const canvas = await html2canvas(tweetRef.current, {
            allowTaint: true,
            backgroundColor: '#000000',
            useCORS: true,
            scrollX: 0,
            scrollY: 0,
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
          });
          
          const imageData = canvas.toDataURL();
          
          // Create the video job data
          const videoJobData = {
            imageData,
            tweetData: tweetData
          };
          
          videoJobs.push(videoJobData);
          console.log(`Screenshot generated for tweet ${i + 1}: @${tweetData.username}`);
        }
      } catch (error) {
        console.error(`Error generating screenshot for tweet ${i}:`, error);
      }
    }
    
    console.log(`Generated ${videoJobs.length} screenshots, starting video generation...`);
    
    // Now start all video jobs at once
    if (videoJobs.length > 0) {
      onVideoGenerate({
        type: 'bulk_batch',
        jobs: videoJobs
      });
    }
    
    setIsGenerating(false);
  };

  const generateTweetImageAndVideo = async (data) => {
    setIsGenerating(true);
    
    try {
      // Create a temporary tweet element for screenshot
      const tempTweetData = {
        name: data.name,
        username: data.username,
        message: data.message,
        theme: 'dark', // Force dark theme for viral effect
        verified: data.verified,
        profileFile: data.profilePicture, // Add the profile picture file
        mediaUrls: data.mediaUrls, // Add all media URLs
        audioSelection: data.audioSelection, // Add the audio selection
        backgroundSelection: data.backgroundSelection, // Add the background selection
        circleOverlays: data.circleOverlays || [], // Add circle overlays
        hasVideo: data.hasVideo, // Add video detection flag
        videoData: data.videoData // Add video data if present
      };
      
      // Wait a bit for the component to render
      await new Promise(resolve => setTimeout(resolve, 100));

      if (tweetRef.current) {
        const canvas = await html2canvas(tweetRef.current, {
          allowTaint: true,
          backgroundColor: '#000000',
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight,
        });
        
        const imageData = canvas.toDataURL();
        
        // Pass the data to generate video directly
        onVideoGenerate({
          imageData,
          tweetData: tempTweetData
        });
      }
    } catch (error) {
      console.error('Error generating tweet image:', error);
      setIsGenerating(false);
    }
  };

  const renderMessage = (message) => {
    if (!message) return 'Tweet content';

    const lines = message.split('\n');
    
    return lines.map((line, lineIndex) => {
      const tokens = line.split(' ');
      const processedTokens = tokens.map((token, tokenIndex) => {
        if (token.match(/^@(\w){1,20}$/)) {
          return <span key={`${lineIndex}-${tokenIndex}`} className="highlight">{token}</span>;
        } else if (token.match(/^@(\w){21,}$/)) {
          return (
            <span key={`${lineIndex}-${tokenIndex}`}>
              <span className="highlight">{token.slice(0, 21)}</span>
              {token.slice(21)}
            </span>
          );
        } else if (token.match(/^@\w+/)) {
          const match = token.match(/^@\w+/)[0];
          const rest = token.replace(match, '');
          return (
            <span key={`${lineIndex}-${tokenIndex}`}>
              <span className="highlight">{match}</span>
              {rest}
            </span>
          );
        } else {
          return <span key={`${lineIndex}-${tokenIndex}`}>{token}</span>;
        }
      });

      return (
        <span key={lineIndex}>
          {processedTokens.reduce((acc, token, index) => {
            if (index === 0) return [token];
            return [...acc, ' ', token];
          }, [])}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <div className="tweet-generator">
      <TwitterImporter 
        onTweetImported={handleTweetImported}
        isGenerating={isGenerating}
        user={user}
      />

      {/* Hidden tweet element for screenshot */}
      {tweetData && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <div ref={tweetRef} className="tweet dark" style={{ 
            width: '500px', 
            padding: '1.5rem', 
            background: '#000000', 
            color: '#ffffff' 
          }}>
            <div className="head">
              <div className="title">
                <img
                  src={(() => {
                    if (tweetData.profileFile) {
                      const imageUrl = URL.createObjectURL(tweetData.profileFile);
                      return imageUrl;
                    } else {
                      return '/assets/silhoutte.png';
                    }
                  })()}
                  alt="avatar"
                  width="48"
                  height="48"
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
                <div className="text">
                  <div className="top">
                    <span className="tweet_name" style={{ fontWeight: '700', fontSize: '0.95rem' }}>
                      {tweetData.name}
                    </span>
                    {tweetData.verified && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="verified"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="#1d9bf0"
                      >
                        <g>
                          <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"></path>
                        </g>
                      </svg>
                    )}
                  </div>
                  <div className="bottom" style={{ fontSize: '0.85rem', opacity: '0.7' }}>
                    @{tweetData.username}
                  </div>
                </div>
              </div>
              <div className="dots" style={{ opacity: '0.7' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                  <g>
                    <circle cx="5" cy="12" r="2"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                    <circle cx="19" cy="12" r="2"></circle>
                  </g>
                </svg>
              </div>
            </div>
            <div className="content" style={{ marginTop: '0.5rem' }}>
              <div className="message" style={{ fontSize: '1.1rem', lineHeight: '1.4', wordWrap: 'break-word' }}>
                {renderMessage(decodeHTMLEntities(tweetData.message))}
              </div>
              {tweetData.mediaUrls && tweetData.mediaUrls.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  {tweetData.mediaUrls.length === 1 ? (
                    <img
                      src={tweetData.mediaUrls[0]}
                      alt="tweet media"
                      style={{ 
                        width: '100%', 
                        borderRadius: '16px',
                        objectFit: 'contain',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <div style={{
                      display: 'grid',
                      gap: '2px',
                      width: '100%',
                      gridTemplateColumns: tweetData.mediaUrls.length === 2 ? 'repeat(2, 1fr)' : 
                                           tweetData.mediaUrls.length === 3 ? '2fr 1fr' : 
                                           'repeat(2, 1fr)',
                      gridTemplateRows: tweetData.mediaUrls.length === 2 ? '1fr' :
                                        tweetData.mediaUrls.length === 3 ? 'repeat(2, 1fr)' :
                                        'repeat(2, 1fr)',
                      aspectRatio: tweetData.mediaUrls.length === 2 ? '2/1' : '2/1',
                      borderRadius: '16px',
                      overflow: 'hidden',
                    }}>
                      {tweetData.mediaUrls.map((url, index) => (
                        <div
                          key={index}
                          style={{
                            gridColumn: tweetData.mediaUrls.length === 3 && index === 0 ? 'span 1' : 'span 1',
                            gridRow: tweetData.mediaUrls.length === 3 && index === 0 ? 'span 2' : 'span 1',
                            overflow: 'hidden',
                            position: 'relative',
                            aspectRatio: '1/1',
                          }}
                        >
                          <img
                            src={url}
                            alt={`tweet media ${index + 1}`}
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '100%',
                              height: 'auto',
                              minHeight: '100%',
                              maxWidth: 'none',
                            }}
                            onLoad={(e) => {
                              const img = e.target;
                              const container = img.parentElement;
                              const containerWidth = container.offsetWidth;
                              const containerHeight = container.offsetHeight;
                              
                              // Calculate the scale to make width fit container
                              const scaleToFitWidth = containerWidth / img.naturalWidth;
                              const scaledHeight = img.naturalHeight * scaleToFitWidth;
                              
                              if (scaledHeight >= containerHeight) {
                                // Image is tall enough, use width-based scaling
                                img.style.width = '100%';
                                img.style.height = 'auto';
                                img.style.left = '50%';
                                img.style.top = '50%';
                                img.style.transform = 'translate(-50%, -50%)';
                              } else {
                                // Image is too short, use height-based scaling
                                img.style.width = 'auto';
                                img.style.height = '100%';
                                img.style.left = '50%';
                                img.style.top = '50%';
                                img.style.transform = 'translate(-50%, -50%)';
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tweetData.quoted_tweet && (
                <div className="quoted-tweet" style={{
                  border: '2px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '16px',
                  padding: '12px',
                  marginTop: '12px',
                  background: 'rgba(255, 255, 255, 0.03)',
                }}>
                  <div className="quoted-head" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '4px'
                  }}>
                    <img
                      src={tweetData.quoted_tweet.author.profilePicture}
                      alt="avatar"
                      width="32"
                      height="32"
                      style={{ 
                        borderRadius: '50%', 
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '4px',
                        minWidth: 0,
                        padding: '2px 0'
                      }}>
                        <span style={{ 
                          fontWeight: '700',
                          fontSize: '15px',
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          lineHeight: '20px',
                          display: 'block',
                          paddingBottom: '2px'
                        }}>{tweetData.quoted_tweet.author.name}</span>
                        {(tweetData.quoted_tweet.author.isBlueVerified || tweetData.quoted_tweet.author.verifiedType) && (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#1d9bf0" style={{ flexShrink: 0, position: 'relative', top: '2px', lineHeight: '20px', display: 'inline-block', verticalAlign: 'middle' }}>
                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"></path>
                          </svg>
                        )}
                        <span style={{ 
                          color: '#71767B',
                          fontSize: '15px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: '20px',
                          display: 'inline-block',
                          paddingBottom: '2px',
                          position: 'relative',
                          top: '0px',
                          verticalAlign: 'middle'
                        }}>@{tweetData.quoted_tweet.author.userName}</span>
                        {tweetData.quoted_tweet.createdAt && (
                          <span style={{ 
                            color: '#71767B',
                            fontSize: '15px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: '20px',
                            display: 'inline-block',
                            paddingBottom: '2px',
                            position: 'relative',
                            top: '0px',
                            verticalAlign: 'middle'
                          }}>· {new Date(tweetData.quoted_tweet.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '15px',
                    lineHeight: '20px',
                    color: '#E7E9EA',
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {renderMessage(decodeHTMLEntities(tweetData.quoted_tweet.text))}
                  </div>
                  {tweetData.quoted_tweet.mediaUrls && tweetData.quoted_tweet.mediaUrls.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {tweetData.quoted_tweet.mediaUrls.length === 1 ? (
                        <img
                          src={tweetData.quoted_tweet.mediaUrls[0]}
                          alt="quoted tweet media"
                          style={{ 
                            width: '100%', 
                            borderRadius: '12px',
                            objectFit: 'contain',
                            height: 'auto',
                            display: 'block',
                            maxHeight: '200px'
                          }}
                        />
                      ) : (
                        <div style={{
                          display: 'grid',
                          gap: '2px',
                          width: '100%',
                          gridTemplateColumns: tweetData.quoted_tweet.mediaUrls.length === 2 ? 'repeat(2, 1fr)' : 
                                               tweetData.quoted_tweet.mediaUrls.length === 3 ? '2fr 1fr' : 
                                               'repeat(2, 1fr)',
                          gridTemplateRows: tweetData.quoted_tweet.mediaUrls.length === 2 ? '1fr' :
                                            tweetData.quoted_tweet.mediaUrls.length === 3 ? 'repeat(2, 1fr)' :
                                            'repeat(2, 1fr)',
                          aspectRatio: tweetData.quoted_tweet.mediaUrls.length === 2 ? '2/1' : '2/1',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          maxHeight: '200px',
                        }}>
                          {tweetData.quoted_tweet.mediaUrls.map((url, index) => (
                            <div
                              key={index}
                              style={{
                                gridColumn: tweetData.quoted_tweet.mediaUrls.length === 3 && index === 0 ? 'span 1' : 'span 1',
                                gridRow: tweetData.quoted_tweet.mediaUrls.length === 3 && index === 0 ? 'span 2' : 'span 1',
                                overflow: 'hidden',
                                position: 'relative',
                                aspectRatio: '1/1',
                              }}
                            >
                              <img
                                src={url}
                                alt={`quoted tweet media ${index + 1}`}
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '100%',
                                  height: 'auto',
                                  minHeight: '100%',
                                  maxWidth: 'none',
                                }}
                                onLoad={(e) => {
                                  const img = e.target;
                                  const container = img.parentElement;
                                  const containerWidth = container.offsetWidth;
                                  const containerHeight = container.offsetHeight;
                                  
                                  // Calculate the scale to make width fit container
                                  const scaleToFitWidth = containerWidth / img.naturalWidth;
                                  const scaledHeight = img.naturalHeight * scaleToFitWidth;
                                  
                                  if (scaledHeight >= containerHeight) {
                                    // Image is tall enough, use width-based scaling
                                    img.style.width = '100%';
                                    img.style.height = 'auto';
                                    img.style.left = '50%';
                                    img.style.top = '50%';
                                    img.style.transform = 'translate(-50%, -50%)';
                                  } else {
                                    // Image is too short, use height-based scaling
                                    img.style.width = 'auto';
                                    img.style.height = '100%';
                                    img.style.left = '50%';
                                    img.style.top = '50%';
                                    img.style.transform = 'translate(-50%, -50%)';
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Restriction Modal */}
      {showAccountRestrictionModal && accountRestrictionData && (
        <div className="modal-overlay" onClick={() => setShowAccountRestrictionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-header-restriction">Account Access Required</h2>
            
            <p>
              Cannot process <strong>{accountRestrictionData.invalidCount}</strong> URL(s) due to account restrictions.
            </p>

            <div className="upgrade-hint">
              <p><strong>Want to generate videos from any account?</strong></p>
              <p>Upgrade your plan to unlock unlimited account access and remove all restrictions!</p>
            </div>

            <p>Your current allowed accounts:</p>
            <ul>
              {accountRestrictionData.allowedAccounts.map((account, index) => (
                <li key={index}>@{account}</li>
              ))}
            </ul>

            <p>Restricted URLs that cannot be processed:</p>
            <ul className="restriction-list">
              {accountRestrictionData.invalidUrls.map((item, index) => (
                <li key={index}>
                  {item.url} - @{item.username}
                </li>
              ))}
            </ul>

            <p>
              Please remove these URLs from your bulk generation or add the accounts to your subscription to proceed.
            </p>

            <div className="modal-actions">
              <button 
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowAccountRestrictionModal(false)}
              >
                Close
              </button>
              <button 
                className="modal-btn modal-btn-primary"
                onClick={() => {
                  setShowAccountRestrictionModal(false);
                  // Trigger upgrade flow - you can customize this
                  onUpgradeRequest();
                }}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quota Limit Modal */}
      {showQuotaModal && quotaModalData && (
        <div className="modal-overlay" onClick={() => setShowQuotaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-header-quota">Monthly Quota Exceeded</h2>
            
            <p>
              You're trying to generate <strong>{quotaModalData.videosRequested}</strong> videos, 
              but you only have <strong>{quotaModalData.videosRemaining}</strong> videos remaining this month.
            </p>

            <div className="quota-details">
              <div className="quota-item">
                <span className="quota-label">Videos Requested:</span>
                <span className="quota-value">{quotaModalData.videosRequested}</span>
              </div>
              <div className="quota-item">
                <span className="quota-label">Videos Remaining:</span>
                <span className="quota-value">{quotaModalData.videosRemaining}</span>
              </div>
              <div className="quota-item excess">
                <span className="quota-label">Videos Over Limit:</span>
                <span className="quota-value">{quotaModalData.excessCount}</span>
              </div>
            </div>

            <div className="upgrade-hint">
              <p><strong>Want unlimited videos?</strong></p>
              <p>Upgrade to Pro and generate as many videos as you want, no monthly limits!</p>
            </div>

            <p>
              Please remove <strong>{quotaModalData.excessCount}</strong> video(s) from your bulk generation or upgrade your plan to continue.
            </p>

            <div className="modal-actions">
              <button 
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowQuotaModal(false)}
              >
                Remove Videos
              </button>
              <button 
                className="modal-btn modal-btn-primary"
                onClick={() => {
                  setShowQuotaModal(false);
                  onUpgradeRequest();
                }}
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TweetGenerator; 